/* Modal de criação de conta comum (ADR-0710). O app é passwordless: a conta nasce
   PRÉ-VERIFICADA e SEM senha — o usuário faz login depois via OTP. super_admin
   informa e-mail (obrigatório) + nome (opcional). Regra de ouro (ADR-0216):
   DialogContent locked enquanto a mutation está em flight (ESC/overlay/X bloqueados).
   Validação via createUserBody. */
import { createUserBody } from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InlineAlert,
  Input,
  Label,
  toast,
} from '@planuze/ui';
import { useEffect, useState } from 'react';
import { useUsersContent } from '../../content/sections/users.js';
import { useCreateUser } from '../../hooks/use-users.js';
import { userErrorMessage } from './user-error.js';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function CreateUserModal({ open, onClose }: Props) {
  const copy = useUsersContent();
  const create = useCreateUser();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setName('');
      setError(null);
    }
  }, [open]);

  /* Validação no submit (molde de CreateStaffDialog): o botão fica sempre clicável e
     a mensagem é exibida quando o payload não passa no createUserBody. `name` só entra
     no payload quando preenchido (é opcional). */
  const submit = (): void => {
    setError(null);
    const trimmedName = name.trim();
    const parsed = createUserBody.safeParse({
      email: email.trim(),
      ...(trimmedName.length > 0 && { name: trimmedName }),
    });
    if (!parsed.success) {
      setError(copy.createModal.validationError);
      return;
    }
    create.mutate(parsed.data, {
      onSuccess: (user) => {
        toast.success(copy.createModal.success(user.email));
        onClose();
      },
      onError: (err) => toast.error(userErrorMessage(err, copy.errors)),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !create.isPending) onClose();
      }}
    >
      <DialogContent locked={create.isPending}>
        <DialogHeader>
          <DialogTitle>{copy.createModal.title}</DialogTitle>
          <DialogDescription>{copy.createModal.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error !== null && <InlineAlert variant="destructive">{error}</InlineAlert>}
          <div className="space-y-1.5">
            <Label htmlFor="cu-email">{copy.createModal.emailLabel}</Label>
            <Input
              id="cu-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={copy.createModal.emailPlaceholder}
              disabled={create.isPending}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cu-name">{copy.createModal.nameLabel}</Label>
            <Input
              id="cu-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={copy.createModal.namePlaceholder}
              disabled={create.isPending}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            {copy.createModal.cancel}
          </Button>
          <Button loading={create.isPending} onClick={submit}>
            {copy.createModal.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
